import { useEffect } from "react";
import type { RefObject } from "react";

const FOCUSABLE =
  "a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex='-1'])";

/** How a scroll region is named: by another element's id (`labelledBy`) or by its own text (`label`). */
export type RegionName = { labelledBy: string; label?: never } | { label: string; labelledBy?: never };

/** An element that scrolls and holds nothing a keyboard can reach is a region a keyboard user cannot
 * scroll (WCAG 2.1.1, axe `scrollable-region-focusable`). Whether it scrolls is a fact of the layout
 * and changes with the window and the data, so it is decided here, in the DOM, where it is measured:
 * when the element overflows and has no focusable descendant it becomes a named, focusable region.
 * One that scrolls but holds a link or a control is left alone - Tab already scrolls it.
 *
 * The attributes are written on the node directly, not through React, because the decision is made
 * from a measurement after layout and a re-render for it would be a second paint of the page. */
export function useScrollableRegionFocus(ref: RefObject<HTMLElement | null>, name: RegionName): void {
  const { label, labelledBy } = name;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const apply = () => {
      const scrolls = el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
      const needs = scrolls && el.querySelector(FOCUSABLE) === null;
      if (needs && el.dataset.scrollRegion !== "1") {
        el.dataset.scrollRegion = "1";
        el.tabIndex = 0;
        el.setAttribute("role", "region");
        if (labelledBy !== undefined) el.setAttribute("aria-labelledby", labelledBy);
        else if (label !== undefined) el.setAttribute("aria-label", label);
      } else if (!needs && el.dataset.scrollRegion === "1") {
        delete el.dataset.scrollRegion;
        el.removeAttribute("tabindex");
        el.removeAttribute("role");
        el.removeAttribute("aria-labelledby");
        el.removeAttribute("aria-label");
      }
    };
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const resize = new ResizeObserver(apply);
    resize.observe(el);
    const mutate = new MutationObserver(apply);
    mutate.observe(el, { childList: true, subtree: true });
    return () => {
      resize.disconnect();
      mutate.disconnect();
    };
  }, [ref, label, labelledBy]);
}
